import { createSignal, onCleanup } from "solid-js";
import {
  createTTSEngine,
  type TTSEngine,
  type TTSText,
  type TTSProvider,
} from "../utils/tts";
import { uiLog } from "../utils/telemetry";

type EnqueueItem = TTSText & { key?: string };

export function useTTS(options?: {
  provider?: TTSProvider;
  storageKey?: string; // enable/voice/rate persistence
  defaultLang?: string;
}) {
  const provider = options?.provider ?? "webspeech";
  const storageKey = options?.storageKey ?? "tts-settings";

  const initialEnabled: boolean = (() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      return !!parsed.enabled;
    } catch {
      return false;
    }
  })();
  const [enabled, setEnabled] = createSignal<boolean>(initialEnabled);

  const [rate, setRate] = createSignal<number>(1);
  const [volume, setVolume] = createSignal<number>(1);
  const [lang, setLang] = createSignal<string>(options?.defaultLang || "");
  const [engine] = createSignal<TTSEngine>(createTTSEngine(provider));
  const [speaking, setSpeaking] = createSignal(false);

  const queue: EnqueueItem[] = [];
  let playingKey: string | undefined;
  let stopRequested = false;

  function persist() {
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          enabled: enabled(),
          rate: rate(),
          volume: volume(),
          lang: lang(),
          provider,
        })
      );
    } catch {}
  }

  function enable() {
    setEnabled(true);
    // Mobile warm-up: create an empty utterance to unlock audio after gesture
    try {
      const synth = (window as any).speechSynthesis as
        | SpeechSynthesis
        | undefined;
      if (synth) {
        const u = new SpeechSynthesisUtterance(" ");
        u.volume = 0; // silent warm-up
        synth.speak(u);
      }
    } catch {}
    persist();
    uiLog("tts_enable", { provider });
  }
  function disable() {
    stop();
    setEnabled(false);
    persist();
    uiLog("tts_disable", { provider });
  }

  function stop() {
    try {
      engine().stop();
    } catch {}
    stopRequested = true;
    setSpeaking(false);
    playingKey = undefined;
    queue.splice(0);
  }

  async function speakNow(item: TTSText) {
    try {
      engine().stop();
    } catch {}
    stopRequested = false;
    setSpeaking(true);
    try {
      await engine().speak({
        text: item.text,
        lang: item.lang || lang() || undefined,
        rate: rate(),
        volume: volume(),
        pitch: item.pitch,
      });
    } catch (e: any) {
      uiLog("tts_error", { provider, message: e?.message || String(e) });
    } finally {
      setSpeaking(false);
    }
  }

  async function drain() {
    if (!enabled()) return;
    if (speaking()) return;
    const next = queue.shift();
    if (!next) return;
    stopRequested = false;
    setSpeaking(true);
    playingKey = next.key;
    try {
      await engine().speak({
        text: next.text,
        lang: next.lang || lang() || undefined,
        rate: rate(),
        volume: volume(),
      });
      if (!stopRequested) setSpeaking(false);
    } catch (e: any) {
      setSpeaking(false);
      uiLog("tts_error", { provider, message: e?.message || String(e) });
    } finally {
      playingKey = undefined;
      queue.length && drain();
    }
  }

  function enqueue(item: EnqueueItem) {
    if (!enabled()) return;
    // Replace existing item with same key (coalesce updates)
    if (item.key) {
      const idx = queue.findIndex((q) => q.key === item.key);
      if (idx >= 0) queue[idx] = item;
      else queue.push(item);
    } else {
      queue.push(item);
    }
    // Interrupt if the same key is currently playing
    if (item.key && playingKey === item.key) {
      try {
        engine().stop();
      } catch {}
      setSpeaking(false);
      playingKey = undefined;
    }
    drain();
  }

  onCleanup(() => stop());

  return {
    // state
    enabled,
    speaking,
    rate,
    volume,
    lang,

    // controls
    enable,
    disable,
    stop,
    enqueue,
    speakNow,

    // settings
    setRate,
    setVolume,
    setLang,
  } as const;
}
